import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { RefreshCw, Download, CheckSquare, Square, Printer, Package, FilePlus } from 'lucide-react';
import AdminLogin from '../components/admin/AdminLogin';
import NotificationControls from '../components/admin/NotificationControls';
import TrackingModal from '../components/admin/TrackingModal';
import OrderCard from '../components/admin/OrderCard';
import { STATUS_CONFIG } from '../components/admin/OrderCard';
import OrderFilters from '../components/admin/OrderFilters';
import SubscribersTab from '../components/admin/SubscribersTab';
import CreateInvoiceModal from '../components/admin/CreateInvoiceModal';
import WholesaleTab from '../components/admin/WholesaleTab';
import ReviewsAdminTab from '../components/admin/ReviewsAdminTab';
import InventoryTab from '../components/admin/InventoryTab';
import EmailHelperTab from '../components/admin/EmailHelperTab';
import AnalyticsTab from '../components/admin/AnalyticsTab';
import CampaignsTab from '../components/admin/CampaignsTab';

const AdminOrders = () => {
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [orders, setOrders] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [wholesaleInvoices, setWholesaleInvoices] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('orders');
  const [updatingOrder, setUpdatingOrder] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [trackingModal, setTrackingModal] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('Australia Post');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const knownOrderIdsRef = useRef(new Set());
  const isInitialLoadRef = useRef(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Filtered orders
  const filteredOrders = useMemo(() => {
    let result = orders;
    if (statusFilter) {
      result = result.filter(o => o.status === statusFilter);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(o =>
        (o.order_id || '').toLowerCase().includes(q) ||
        (o.customer_name || '').toLowerCase().includes(q) ||
        (o.customer_email || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [orders, searchQuery, statusFilter]);

  // Notification sound
  const playNotificationSound = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const notes = [523.25, 659.25, 783.99];
      notes.forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.12, ctx.currentTime + i * 0.12);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.6);
        osc.start(ctx.currentTime + i * 0.12);
        osc.stop(ctx.currentTime + i * 0.12 + 0.6);
      });
    } catch (e) { /* audio unavailable */ }
  }, [soundEnabled]);

  const showDesktopNotification = useCallback((freshOrders) => {
    if (!notificationsEnabled || !('Notification' in window) || Notification.permission !== 'granted') return;
    const count = freshOrders.length;
    const first = freshOrders[0];
    new Notification(
      count === 1 ? `New Order: ${first.order_id}` : `${count} New Orders!`,
      {
        body: count === 1
          ? `${first.customer_name || 'Customer'} — $${first.total.toFixed(2)}`
          : `Latest: ${first.customer_name || 'Customer'} — $${first.total.toFixed(2)}`,
        icon: '/favicon.ico'
      }
    );
  }, [notificationsEnabled]);

  useEffect(() => {
    if (authenticated && notificationsEnabled && 'Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, [authenticated, notificationsEnabled]);

  // Auto-refresh polling
  useEffect(() => {
    if (!authenticated || !autoRefresh) return;
    const interval = setInterval(async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/orders?password=${encodeURIComponent(password)}`
        );
        if (!response.ok) return;
        const data = await response.json();
        const freshOrders = data.filter(o => !knownOrderIdsRef.current.has(o.order_id));
        if (!isInitialLoadRef.current && freshOrders.length > 0) {
          playNotificationSound();
          showDesktopNotification(freshOrders);
          setNewOrderIds(prev => {
            const next = new Set(prev);
            freshOrders.forEach(o => next.add(o.order_id));
            return next;
          });
          setTimeout(() => {
            setNewOrderIds(prev => {
              const next = new Set(prev);
              freshOrders.forEach(o => next.delete(o.order_id));
              return next;
            });
          }, 30000);
        }
        knownOrderIdsRef.current = new Set(data.map(o => o.order_id));
        isInitialLoadRef.current = false;
        setOrders(data);
      } catch (err) { /* silent retry */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [authenticated, autoRefresh, password, playNotificationSound, showDesktopNotification]);

  // Order actions
  const updateOrderStatus = async (orderId, newStatus, trackingInfo) => {
    setUpdatingOrder(orderId);
    try {
      const body = { password, status: newStatus };
      if (trackingInfo) {
        body.tracking_number = trackingInfo.tracking_number;
        body.carrier = trackingInfo.carrier;
      }
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/status`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }
      );
      if (response.ok) {
        setOrders(prev => prev.map(o => o.order_id === orderId ? {
          ...o, status: newStatus,
          ...(trackingInfo ? { tracking_number: trackingInfo.tracking_number, carrier: trackingInfo.carrier } : {})
        } : o));
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setUpdatingOrder(null);
    }
  };

  const handleStatusClick = (orderId, newStatus) => {
    if (newStatus === 'shipped') {
      setTrackingModal(orderId);
      setTrackingNumber('');
      setCarrier('Australia Post');
    } else {
      updateOrderStatus(orderId, newStatus);
    }
  };

  const submitTracking = () => {
    if (trackingModal) {
      updateOrderStatus(trackingModal, 'shipped', { tracking_number: trackingNumber, carrier });
      setTrackingModal(null);
    }
  };

  const bulkUpdateStatus = async (newStatus) => {
    if (selectedOrders.size === 0) return;
    setBulkUpdating(true);
    const ids = Array.from(selectedOrders);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders/bulk-status`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password, status: newStatus, order_ids: ids }) }
      );
      if (response.ok) {
        const data = await response.json();
        setOrders(prev => prev.map(o => data.updated_ids.includes(o.order_id) ? { ...o, status: newStatus } : o));
        setSelectedOrders(new Set());
      }
    } catch (err) {
      console.error('Bulk update failed:', err);
    } finally {
      setBulkUpdating(false);
    }
  };

  const toggleSelect = (orderId) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId); else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrders.size === filteredOrders.length) {
      setSelectedOrders(new Set());
    } else {
      setSelectedOrders(new Set(filteredOrders.map(o => o.order_id)));
    }
  };

  // Data fetching
  const fetchOrders = async (pwd) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/orders?password=${encodeURIComponent(pwd || password)}`
      );
      if (!response.ok) {
        if (response.status === 401) { setError('Incorrect password'); setAuthenticated(false); return; }
        throw new Error('Failed to fetch orders');
      }
      const data = await response.json();
      setOrders(data);
      knownOrderIdsRef.current = new Set(data.map(o => o.order_id));
      isInitialLoadRef.current = false;
      setAuthenticated(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribers?password=${encodeURIComponent(password)}`
      );
      if (response.ok) { const data = await response.json(); setSubscribers(data); }
    } catch (err) {
      console.error('Failed to fetch subscribers:', err);
    }
  };

  const fetchWholesale = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/wholesale/invoices?password=${encodeURIComponent(password)}`
      );
      if (response.ok) { const data = await response.json(); setWholesaleInvoices(data); }
    } catch (err) {
      console.error('Failed to fetch wholesale invoices:', err);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/reviews/admin?password=${encodeURIComponent(password)}`
      );
      if (response.ok) { const data = await response.json(); setReviews(data); }
    } catch (err) {
      console.error('Failed to fetch reviews:', err);
    }
  };

  const handleLogin = (e) => { e.preventDefault(); fetchOrders(); };

  useEffect(() => {
    if (!authenticated) return;
    fetchSubscribers();
    fetchWholesale();
    fetchReviews();
  }, [authenticated]);

  useEffect(() => {
    if (authenticated && (activeTab === 'subscribers' || activeTab === 'campaigns')) fetchSubscribers();
    if (authenticated && activeTab === 'wholesale') fetchWholesale();
    if (authenticated && activeTab === 'reviews') fetchReviews();
  }, [authenticated, activeTab]);

  // CSV exports
  const exportSubscribersCSV = () => {
    const csv = "Email,Subscribed Date\n" + subscribers.map(s =>
      `${s.email},${new Date(s.subscribed_at).toLocaleDateString('en-AU')}`
    ).join("\n");
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'rickysbees_subscribers.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const exportOrdersCSV = () => {
    const source = filteredOrders;
    const header = "Order ID,Date,Customer,Email,Phone,Status,Payment,Shipping Method,Address,Items,Subtotal,Discount,Shipping,Total\n";
    const rows = source.map(o => {
      const date = new Date(o.created_at).toLocaleDateString('en-AU');
      const addr = o.shipping_address ? `${o.shipping_address.street || ''} ${o.shipping_address.suburb || ''} ${o.shipping_address.state || ''} ${o.shipping_address.postcode || ''}`.trim() : 'Pickup';
      const items = o.items.map(i => `${i.name} (${i.size}) x${i.quantity}`).join('; ');
      return `${o.order_id},${date},"${o.customer_name || ''}",${o.customer_email || ''},${o.customer_phone || ''},${o.status},${o.payment_method},${o.shipping_method || ''},"${addr}","${items}",${o.subtotal.toFixed(2)},${(o.discount || 0).toFixed(2)},${o.shipping.toFixed(2)},${o.total.toFixed(2)}`;
    }).join("\n");
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `rickysbees_orders_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Print Picking List
  const printPickingList = () => {
    const ordersToPrint = selectedOrders.size > 0
      ? orders.filter(o => selectedOrders.has(o.order_id))
      : orders.filter(o => o.status === 'confirmed' || o.status === 'processing');

    const html = `
      <html>
      <head>
        <title>Packing List — Ricky's Bees</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: Arial, sans-serif; color: #2D4B38; padding: 20px; }
          .order { page-break-after: always; border: 2px solid #2D4B38; border-radius: 12px; padding: 28px; margin-bottom: 24px; max-width: 700px; margin-left: auto; margin-right: auto; }
          .order:last-child { page-break-after: avoid; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 16px; border-bottom: 2px solid #2D4B38; }
          .logo { font-size: 22px; font-weight: bold; }
          .order-id { font-size: 24px; font-weight: bold; }
          .order-date { font-size: 13px; color: #666; margin-top: 4px; }
          .badge { display: inline-block; padding: 3px 12px; border-radius: 12px; font-size: 12px; font-weight: bold; margin-left: 8px; }
          .pickup { background: #f0e6ff; color: #6b21a8; }
          .postage { background: #e0f2fe; color: #0369a1; }
          .status { background: #2D4B38; color: white; }
          .section { margin-bottom: 20px; }
          .section-title { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #888; margin-bottom: 8px; font-weight: bold; }
          .details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
          .detail-box { background: #faf7f2; border-radius: 8px; padding: 14px; }
          .detail-label { font-size: 11px; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 4px; }
          .detail-value { font-size: 14px; font-weight: 600; }
          .detail-sub { font-size: 13px; color: #444; margin-top: 2px; }
          table { width: 100%; border-collapse: collapse; }
          th { text-align: left; padding: 10px 12px; border-bottom: 2px solid #2D4B38; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
          td { padding: 10px 12px; border-bottom: 1px solid #eee; font-size: 14px; }
          .item-name { font-weight: 600; }
          .totals { margin-top: 12px; text-align: right; }
          .total-row { display: flex; justify-content: flex-end; gap: 24px; padding: 4px 12px; font-size: 14px; }
          .total-row.grand { font-size: 18px; font-weight: bold; border-top: 2px solid #2D4B38; padding-top: 8px; margin-top: 8px; }
          .checkbox-row { display: flex; align-items: center; gap: 8px; margin-top: 20px; padding-top: 16px; border-top: 1px dashed #ccc; }
          .checkbox { width: 20px; height: 20px; border: 2px solid #2D4B38; display: inline-block; border-radius: 3px; flex-shrink: 0; }
          .checkbox-label { font-size: 13px; color: #666; }
          @media print { body { padding: 0; } .order { border: 1px solid #ccc; } }
        </style>
      </head>
      <body>
        ${ordersToPrint.map(o => {
          const isPickup = o.shipping_method === 'pickup';
          const addr = o.shipping_address
            ? (o.shipping_address.pickup_location
              ? 'Pickup — ' + o.shipping_address.pickup_location
              : [o.shipping_address.name || '', o.shipping_address.street || '', o.shipping_address.street2 || '', [o.shipping_address.suburb, o.shipping_address.state, o.shipping_address.postcode].filter(Boolean).join(' ')].filter(Boolean).join('<br>'))
            : '';
          const billingAddr = o.billing_address
            ? [o.billing_address.street || '', o.billing_address.street2 || '', [o.billing_address.suburb, o.billing_address.state, o.billing_address.postcode].filter(Boolean).join(' ')].filter(Boolean).join('<br>')
            : '';
          return `
          <div class="order">
            <div class="header">
              <div>
                <div class="logo">Ricky's Bees</div>
                <div class="order-date">${new Date(o.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
              </div>
              <div style="text-align:right">
                <div class="order-id">${o.order_id}</div>
                <div>
                  <span class="badge status">${(o.status || 'confirmed').toUpperCase()}</span>
                  <span class="badge ${isPickup ? 'pickup' : 'postage'}">${isPickup ? 'PICKUP' : 'POSTAGE'}</span>
                </div>
              </div>
            </div>
            <div class="section">
              <div class="section-title">Customer Details</div>
              <div class="details-grid">
                <div class="detail-box"><div class="detail-label">Name</div><div class="detail-value">${o.customer_name || 'N/A'}</div></div>
                <div class="detail-box"><div class="detail-label">Phone</div><div class="detail-value">${o.customer_phone || 'N/A'}</div></div>
                <div class="detail-box"><div class="detail-label">Email</div><div class="detail-value">${o.customer_email || 'N/A'}</div></div>
                <div class="detail-box"><div class="detail-label">Payment</div><div class="detail-value">${o.payment_method === 'stripe' ? 'Card (Stripe)' : 'PayPal'}</div></div>
              </div>
            </div>
            <div class="section">
              <div class="details-grid">
                <div class="detail-box"><div class="detail-label">${isPickup ? 'Pickup Location' : 'Shipping Address'}</div><div class="detail-sub">${addr || 'N/A'}</div></div>
                ${billingAddr ? `<div class="detail-box"><div class="detail-label">Billing Address</div><div class="detail-sub">${billingAddr}</div></div>` : ''}
              </div>
            </div>
            ${o.tracking_number ? `<div class="section"><div class="detail-box" style="background:#f0e6ff"><div class="detail-label">Tracking Number</div><div class="detail-value">${o.tracking_number} ${o.carrier ? '(' + o.carrier + ')' : ''}</div></div></div>` : ''}
            <div class="section">
              <div class="section-title">Order Items</div>
              <table>
                <thead><tr><th style="width:30px"></th><th>Product</th><th>Size</th><th style="text-align:center">Qty</th><th style="text-align:right">Price</th></tr></thead>
                <tbody>
                  ${o.items.map(i => `<tr><td><span class="checkbox"></span></td><td class="item-name">${i.name}</td><td>${i.size}</td><td style="text-align:center">${i.quantity}</td><td style="text-align:right">$${(i.price * i.quantity).toFixed(2)}</td></tr>`).join('')}
                </tbody>
              </table>
              <div class="totals">
                <div class="total-row"><span>Subtotal</span><span>$${o.subtotal.toFixed(2)}</span></div>
                ${o.discount > 0 ? `<div class="total-row" style="color:green"><span>Discount (${o.promo_code || 'BEE10'})</span><span>-$${o.discount.toFixed(2)}</span></div>` : ''}
                <div class="total-row"><span>Shipping</span><span>${o.shipping === 0 ? 'FREE' : '$' + o.shipping.toFixed(2)}</span></div>
                <div class="total-row grand"><span>Total</span><span>$${o.total.toFixed(2)}</span></div>
              </div>
            </div>
            <div class="checkbox-row">
              <span class="checkbox"></span><span class="checkbox-label">Packed & Ready</span>
            </div>
          </div>`;
        }).join('')}
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  if (!authenticated) {
    return <AdminLogin password={password} setPassword={setPassword} loading={loading} error={error} onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen pb-24 pt-56">
      <div className="max-w-6xl mx-auto px-4 md:px-8 lg:px-16">
        {/* Tabs */}
        <div className="flex space-x-4 mb-8 border-b border-border overflow-x-auto" data-testid="admin-tab-bar">
          <button onClick={() => setActiveTab('orders')}
            className={`pb-3 px-2 text-lg font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'orders' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-orders">
            Orders ({orders.length})
          </button>
          <button onClick={() => setActiveTab('wholesale')}
            className={`pb-3 px-2 text-lg font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'wholesale' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-wholesale">
            Wholesale ({wholesaleInvoices.length})
          </button>
          <button onClick={() => setActiveTab('analytics')}
            className={`pb-3 px-2 text-lg font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'analytics' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-analytics">
            Analytics
          </button>
          <button onClick={() => setActiveTab('inventory')}
            className={`pb-3 px-2 text-lg font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'inventory' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-inventory">
            Inventory
          </button>
          <button onClick={() => setActiveTab('reviews')}
            className={`pb-3 px-2 text-lg font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'reviews' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-reviews">
            Reviews ({reviews.length})
          </button>
          <button onClick={() => setActiveTab('email-ai')}
            className={`pb-3 px-2 text-lg font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'email-ai' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-email-ai">
            Email AI
          </button>
          <button onClick={() => setActiveTab('campaigns')}
            className={`pb-3 px-2 text-lg font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'campaigns' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-campaigns">
            Campaigns
          </button>
          <button onClick={() => setActiveTab('subscribers')}
            className={`pb-3 px-2 text-lg font-semibold transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === 'subscribers' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'}`}
            data-testid="tab-subscribers">
            Subscribers ({subscribers.length})
          </button>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <>
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Orders</h1>
              <div className="flex space-x-3">
                <button onClick={() => { setEditingInvoice(null); setShowInvoiceModal(true); }} className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors" data-testid="create-invoice-button">
                  <FilePlus className="w-4 h-4" /><span className="text-sm">Create Invoice</span>
                </button>
                <button onClick={() => fetchOrders()} className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg border border-border hover:bg-muted transition-colors" data-testid="refresh-orders-button">
                  <RefreshCw className="w-4 h-4" /><span className="text-sm">Refresh</span>
                </button>
                {orders.length > 0 && (
                  <button onClick={exportOrdersCSV} className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid="export-orders-button">
                    <Download className="w-4 h-4" /><span className="text-sm">Export CSV</span>
                  </button>
                )}
                {orders.length > 0 && (
                  <button onClick={printPickingList} className="inline-flex items-center space-x-2 px-4 py-2 rounded-lg border-2 border-primary text-primary hover:bg-primary/5 transition-colors" data-testid="print-picking-list-button">
                    <Printer className="w-4 h-4" /><span className="text-sm">{selectedOrders.size > 0 ? `Print Selected (${selectedOrders.size})` : 'Print Picking List'}</span>
                  </button>
                )}
              </div>
            </div>

            <NotificationControls
              autoRefresh={autoRefresh} setAutoRefresh={setAutoRefresh}
              notificationsEnabled={notificationsEnabled} setNotificationsEnabled={setNotificationsEnabled}
              soundEnabled={soundEnabled} setSoundEnabled={setSoundEnabled}
            />

            <OrderFilters
              searchQuery={searchQuery} setSearchQuery={setSearchQuery}
              statusFilter={statusFilter} setStatusFilter={setStatusFilter}
              filteredCount={filteredOrders.length} totalCount={orders.length}
            />

            {/* Bulk Action Bar */}
            {filteredOrders.length > 0 && (
              <div className="flex items-center justify-between p-4 rounded-xl border border-border bg-muted/30 mb-6" data-testid="bulk-action-bar">
                <div className="flex items-center space-x-3">
                  <button onClick={toggleSelectAll} className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary transition-colors" data-testid="select-all-orders">
                    {selectedOrders.size === filteredOrders.length && filteredOrders.length > 0 ? <CheckSquare className="w-5 h-5 text-primary" /> : <Square className="w-5 h-5" />}
                    <span>{selectedOrders.size === filteredOrders.length && filteredOrders.length > 0 ? 'Deselect All' : 'Select All'}</span>
                  </button>
                  {selectedOrders.size > 0 && (
                    <span className="text-sm text-foreground/60">{selectedOrders.size} selected</span>
                  )}
                </div>
                {selectedOrders.size > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium text-foreground/70 mr-1">Bulk Update:</span>
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <button key={key} onClick={() => bulkUpdateStatus(key)} disabled={bulkUpdating}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all bg-background border border-border text-foreground/60 hover:${cfg.color.split(' ')[0]} hover:${cfg.color.split(' ')[1]} disabled:opacity-50`}
                        data-testid={`bulk-set-${key}`}>
                        {bulkUpdating ? '...' : cfg.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {filteredOrders.length === 0 ? (
              <div className="text-center py-16">
                <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg text-foreground/70">{orders.length === 0 ? 'No orders yet' : 'No orders match your filters'}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order, index) => (
                  <OrderCard
                    key={order.order_id}
                    order={order}
                    index={index}
                    isSelected={selectedOrders.has(order.order_id)}
                    isNew={newOrderIds.has(order.order_id)}
                    updatingOrder={updatingOrder}
                    onToggleSelect={toggleSelect}
                    onStatusClick={handleStatusClick}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'wholesale' && (
          <WholesaleTab
            invoices={wholesaleInvoices}
            password={password}
            onRefresh={fetchWholesale}
            onCreate={() => { setEditingInvoice(null); setShowInvoiceModal(true); }}
            onEdit={(inv) => { setEditingInvoice(inv); setShowInvoiceModal(true); }}
          />
        )}
        {activeTab === 'analytics' && <AnalyticsTab password={password} />}
        {activeTab === 'inventory' && <InventoryTab password={password} />}
        {activeTab === 'reviews' && (
          <ReviewsAdminTab reviews={reviews} password={password} onRefresh={fetchReviews} />
        )}
        {activeTab === 'email-ai' && <EmailHelperTab password={password} />}
        {activeTab === 'campaigns' && <CampaignsTab password={password} subscriberCount={subscribers.length} />}
        {activeTab === 'subscribers' && <SubscribersTab subscribers={subscribers} onRefresh={fetchSubscribers} onExportCSV={exportSubscribersCSV} password={password} />}
      </div>

      {showInvoiceModal && <CreateInvoiceModal onClose={() => { setShowInvoiceModal(false); setEditingInvoice(null); }} password={password} onSaved={fetchWholesale} invoice={editingInvoice} />}

      <TrackingModal
        trackingModal={trackingModal}
        trackingNumber={trackingNumber} setTrackingNumber={setTrackingNumber}
        carrier={carrier} setCarrier={setCarrier}
        onSubmit={submitTracking}
        onSkip={() => { updateOrderStatus(trackingModal, 'shipped'); setTrackingModal(null); }}
        onClose={() => setTrackingModal(null)}
      />
    </div>
  );
};

export default AdminOrders;
